/*
 * PDP Fit Section
 *
 * Two custom elements plus the Fit Finder popup trigger:
 *   fit-finder       three questions, then a recommended size
 *   fit-chat-status  online/offline indicator derived from staffed hours
 *
 * The popup reuses the theme's fav-popup shell (overlay, fav-popup-open body
 * class, trapFocus/removeTrapFocus from global.js) rather than the
 * popup-trigger element, because the markup is already in the page and does
 * not need the AJAX section clone that popup-trigger performs.
 */

(function () {
  'use strict';

  var HALF_SIZE = 0.5;

  /* Fit Finder popup
     ====================================================================== */

  var lastFocusedElement = null;

  function openPopup(popup, opener) {
    lastFocusedElement = opener || document.activeElement;
    document.body.classList.add('fav-popup-open');
    popup.classList.add('active');

    // One popup serves both placements, so each opening resets it and picks up
    // however many reasons that trigger asks for.
    var finder = popup.querySelector('fit-finder');
    if (finder && typeof finder.restart === 'function') {
      finder.reasonLimit = parseInt(opener && opener.dataset.fitReasonLimit, 10) || 3;
      finder.restart();
    }

    // Land on the first answer rather than the close button, so the dialog opens
    // on the thing you came to do. Close stays first in the tab order regardless.
    var step = popup.querySelector('[data-fit-step]:not([hidden])');
    var focusTarget =
      (step && step.querySelector('[data-fit-answer]')) ||
      popup.querySelector('[data-close]') ||
      popup;

    if (typeof trapFocus === 'function') trapFocus(popup, focusTarget);
  }

  function closePopup(popup) {
    document.body.classList.remove('fav-popup-open');
    popup.classList.remove('active');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(lastFocusedElement);
    } else if (lastFocusedElement) {
      lastFocusedElement.focus();
    }
    lastFocusedElement = null;
  }

  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-fit-finder-open]');
    if (opener) {
      var popup = document.getElementById(opener.dataset.fitFinderOpen);
      if (popup) {
        event.preventDefault();
        openPopup(popup, opener);
      }
      return;
    }

    var popupEl = event.target.closest('[data-fit-finder-popup]');
    if (!popupEl) return;

    // Close on the X, or on a click that lands on the backdrop itself.
    if (event.target.closest('[data-close]') || event.target === popupEl) {
      event.preventDefault();
      closePopup(popupEl);
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;

    var open = document.querySelector('[data-fit-finder-popup].active');
    if (open) {
      event.preventDefault();
      closePopup(open);
    }
  });

  /* Chat entry point.
   *
   * Gorgias loads asynchronously and is hidden on mobile by store settings.
   * Wait for its API, then allow mobile visibility on an explicit chat request.
   * Clicking its launcher iframe does not activate the button inside it. */
  var chatOpenPending = false;

  document.addEventListener('click', function (event) {
    if (!event.target.closest('[data-fit-chat-open]')) return;

    event.preventDefault();
    if (chatOpenPending) return;

    if (!window.GorgiasChat) {
      var widget = document.querySelector('[data-chat-open]');
      if (widget && widget.tagName !== 'IFRAME') {
        widget.click();
        return;
      }
    }

    chatOpenPending = true;
    var chatReady = window.GorgiasChat
      ? window.GorgiasChat.init()
      : new Promise(function (resolve) {
          window.addEventListener('gorgias-widget-loaded', resolve, { once: true });
        });

    chatReady.then(function () {
      window.GorgiasChat.hideOnMobile(false);
      window.GorgiasChat.open();
    }).catch(function (error) {
      console.warn('Fit Section: could not open chat', error);
    }).finally(function () {
      chatOpenPending = false;
    });
  });

  /* fit-finder
     ====================================================================== */

  class FitFinder extends HTMLElement {
    connectedCallback() {
      this.config = this.readConfig();
      if (!this.config) return;

      this.answers = { size: null, brand: null, preference: null };
      this.step = 1;
      this.reasonLimit = 3;

      this.steps = this.querySelectorAll('[data-fit-step]');
      this.stepCount = this.querySelector('[data-fit-step-count]');
      this.progressFill = this.querySelector('[data-fit-progress-fill]');
      this.resultEl = this.querySelector('[data-fit-result]');
      this.backButton = this.querySelector('[data-fit-back]');

      this.addEventListener('click', this.onClick.bind(this));
    }

    readConfig() {
      var script = document.getElementById(this.dataset.payload);
      if (!script) return null;

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.warn('Fit Finder: could not parse configuration', error);
        return null;
      }
    }

    onClick(event) {
      var option = event.target.closest('[data-fit-answer]');
      if (option) return this.onAnswer(option);

      if (event.target.closest('[data-fit-restart]')) return this.restart();
      if (event.target.closest('[data-fit-back]')) return this.goBack();
      if (event.target.closest('[data-fit-add-to-bag]')) return this.addToBag();
      if (event.target.closest('[data-fit-notify]')) return this.notifyMe();
    }

    onAnswer(option) {
      var field = option.dataset.fitAnswer;
      this.answers[field] = option.dataset.fitValue;

      var group = option.parentElement;
      group.querySelectorAll('[data-fit-answer]').forEach(function (button) {
        button.setAttribute('aria-pressed', String(button === option));
      });

      if (this.step < 3) {
        this.goTo(this.step + 1);
      } else {
        this.showResult();
      }
    }

    goTo(step) {
      this.step = step;

      this.steps.forEach(function (el) {
        el.hidden = Number(el.dataset.fitStep) !== step;
      });

      if (this.stepCount) this.stepCount.textContent = 'Step ' + step + ' of 3';

      // The bar fills behind the current step: empty on step 1, a third on
      // step 2, two thirds on step 3. The result fills it completely.
      if (this.progressFill) {
        this.progressFill.style.width = Math.round(((step - 1) / 3) * 100) + '%';
      }

      this.resultEl.hidden = true;
      this.backButton.hidden = step === 1;
    }

    goBack() {
      if (this.step > 1) this.goTo(this.step - 1);
    }

    restart() {
      this.answers = { size: null, brand: null, preference: null };
      this.querySelectorAll('[data-fit-answer]').forEach(function (button) {
        button.setAttribute('aria-pressed', 'false');
      });
      this.goTo(1);
    }

    /* Recommendation
       -------------------------------------------------------------------- */

    brandFor(name) {
      if (!name) return null;

      return this.config.brands.filter(function (brand) {
        return brand.name === name;
      })[0] || null;
    }

    /* Offsets are in half-size steps, so a step of 1 moves half a size. */
    calculate() {
      var config = this.config;
      var brand = this.brandFor(this.answers.brand);
      var brandSteps = brand ? Number(brand.offset) || 0 : 0;

      var preference = this.answers.preference || 'standard';
      var preferenceSteps = Number(config.preferences[preference]) || 0;

      var target = Number(this.answers.size) + (brandSteps + preferenceSteps) * HALF_SIZE;

      var confidence = config.confidence.base
        - Math.abs(brandSteps) * config.confidence.brandPenalty
        - this.preferencePenalty(preference);

      // An unknown brand means we are working from the usual size alone.
      if (this.answers.brand === '') confidence -= config.confidence.brandPenalty;

      confidence = Math.max(0, Math.min(100, Math.round(confidence)));

      return {
        index: this.nearestSizeIndex(target),
        confidence: confidence,
        confident: confidence >= config.confidence.floor,
        brand: brand,
        brandSteps: brandSteps,
        preference: preference,
        preferenceSteps: preferenceSteps
      };
    }

    preferencePenalty(preference) {
      var penalty = this.config.confidence.preferencePenalty;

      if (preference === 'roomy') return penalty;
      if (preference === 'snug') return penalty / 2;
      return 0;
    }

    nearestSizeIndex(target) {
      var sizes = this.config.sizes;
      var best = 0;
      var bestDistance = Infinity;

      for (var i = 0; i < sizes.length; i++) {
        var distance = Math.abs(parseFloat(sizes[i]) - target);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = i;
        }
      }

      return best;
    }

    reasonsFor(result) {
      var config = this.config;
      var reasons = [];

      if (result.brand) {
        if (result.brandSteps === 0) {
          reasons.push('Your ' + result.brand.name + ' size maps straight across to ours.');
        } else {
          reasons.push(
            result.brand.name + ' runs ' + describeSteps(Math.abs(result.brandSteps)) + ' ' +
            (result.brandSteps > 0 ? 'small' : 'large') + ' against our last, so we adjusted for it.'
          );
        }
      } else if (this.answers.brand === '') {
        reasons.push('You were not sure of your current brand, so we started from your usual size.');
      }

      if (result.preferenceSteps !== 0) {
        reasons.push(
          'You prefer a ' + result.preference + ' fit, so we ' +
          (result.preferenceSteps > 0 ? 'added ' : 'took off ') +
          describeSteps(Math.abs(result.preferenceSteps)) + '.'
        );
      }

      if (config.copy.trueToSize > 0) {
        reasons.push(config.copy.trueToSize + '% of customers say this style is true to size.');
      }

      if (config.copy.widthRating) {
        reasons.push('This last is a ' + config.copy.widthRating.toLowerCase() + ' width.');
      }

      return reasons.slice(0, this.reasonLimit);
    }

    showResult() {
      var result = this.calculate();
      var sizes = this.config.sizes;
      var size = sizes[result.index];

      this.steps.forEach(function (el) { el.hidden = true; });
      if (this.stepCount) this.stepCount.textContent = 'Result';
      if (this.progressFill) this.progressFill.style.width = '100%';
      this.backButton.hidden = true;
      this.resultEl.hidden = false;

      // Below the confidence floor we show a range rather than a single size.
      var display = size;
      if (!result.confident) {
        var low = sizes[Math.max(0, result.index - 1)];
        var high = sizes[Math.min(sizes.length - 1, result.index + 1)];
        display = low === high ? size : low + '\u2013' + high;
      }

      this.querySelector('[data-fit-result-size]').textContent = display;
      this.querySelector('[data-fit-result-confidence]').textContent = result.confidence + '%';

      var confidenceFill = this.querySelector('[data-fit-confidence-fill]');
      if (confidenceFill) confidenceFill.style.width = result.confidence + '%';

      var note = this.querySelector('[data-fit-result-note]');
      if (note) {
        note.hidden = result.confident;
        note.textContent = result.confident
          ? ''
          : 'Between sizes. Chat with a specialist if you would like a second opinion.';
      }

      var list = this.querySelector('[data-fit-reasons]');
      list.innerHTML = '';
      this.reasonsFor(result).forEach(function (reason) {
        var li = document.createElement('li');
        li.textContent = reason;
        list.appendChild(li);
      });

      this.renderAddToBag(result.confident ? size : null);
    }

    /* Add to bag
       -------------------------------------------------------------------- */

    sizeInputFor(size) {
      var picker = document.querySelector('variant-radios');
      if (!picker || size === null) return null;

      var inputs = picker.querySelectorAll('.option-size input[type="radio"]');

      for (var i = 0; i < inputs.length; i++) {
        if (parseFloat(inputs[i].value) === parseFloat(size)) return inputs[i];
      }

      return null;
    }

    /* Size lists are strings from the metaobject and strings from the variant
       options, and the two spell half sizes differently often enough ("10.5"
       against "10.50") that they are compared numerically. */
    listHas(list, size) {
      if (!list || size === null) return false;

      return list.some(function (candidate) {
        return parseFloat(candidate) === parseFloat(size);
      });
    }

    renderAddToBag(size) {
      var addButton = this.querySelector('[data-fit-add-to-bag]');
      var notifyButton = this.querySelector('[data-fit-notify]');
      var input = this.sizeInputFor(size);

      var stocked = this.listHas(this.config.stockedSizes, size);
      var available = this.listHas(this.config.availableSizes, size);

      // Three outcomes: buy it, be told when it returns, or nothing at all
      // when we are showing a range or the size was never made in this style.
      addButton.hidden = !(input && available);
      notifyButton.hidden = !(input && stocked && !available);

      if (!addButton.hidden) {
        addButton.textContent = this.config.copy.ctaLabel.replace('[size]', size);
        addButton.dataset.fitSize = size;
      }

      if (!notifyButton.hidden) {
        notifyButton.textContent = 'Notify me when size ' + size + ' is back';
        notifyButton.dataset.fitSize = size;
      }
    }

    /* Point the theme's picker at a size, so price, availability and anything
       else keyed off the selected variant follow along. */
    selectSize(size) {
      var input = this.sizeInputFor(size);
      if (!input) return null;

      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));

      return input;
    }

    addToBag() {
      var button = this.querySelector('[data-fit-add-to-bag]');
      if (!this.selectSize(button.dataset.fitSize)) return;

      var popup = this.closest('[data-fit-finder-popup]');
      if (popup) closePopup(popup);

      var submit = document.querySelector('product-form [data-add-to-cart]');
      if (submit) submit.click();
    }

    notifyMe() {
      var button = this.querySelector('[data-fit-notify]');
      if (!this.selectSize(button.dataset.fitSize)) return;

      var popup = this.closest('[data-fit-finder-popup]');
      if (popup) closePopup(popup);

      // The variant change above is what tells Klaviyo which size to watch, so
      // the trigger is clicked on the next frame once the picker has settled.
      requestAnimationFrame(function () {
        var trigger = document.querySelector('.klaviyo-bis-trigger');
        if (trigger) trigger.click();
      });
    }
  }

  function describeSteps(steps) {
    if (steps === 1) return 'half a size';
    if (steps === 2) return 'a full size';
    return (steps * HALF_SIZE) + ' sizes';
  }

  customElements.define('fit-finder', FitFinder);

  /* fit-chat-status
     ====================================================================== */

  var DAY_NAMES = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

  class FitChatStatus extends HTMLElement {
    connectedCallback() {
      this.indicator = this.querySelector('[data-fit-chat-indicator]');
      this.label = this.querySelector('[data-fit-chat-label]');

      var tile = this.closest('.fit-tile') || document;
      this.onlineCopy = tile.querySelector('[data-fit-chat-online]');
      this.offlineCopy = tile.querySelector('[data-fit-chat-offline]');

      this.update();

      // Re-check each minute so a page left open crosses the boundary.
      this.timer = setInterval(this.update.bind(this), 60000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    update() {
      var online = this.isStaffed();

      this.dataset.status = online ? 'online' : 'offline';
      this.label.textContent = online ? 'Online now' : 'Offline';

      if (this.onlineCopy) this.onlineCopy.hidden = !online;
      if (this.offlineCopy) this.offlineCopy.hidden = online;
    }

    isStaffed() {
      var days = parseDays(this.dataset.days);
      var start = parseTime(this.dataset.start);
      var end = parseTime(this.dataset.end);

      // Without a complete schedule we cannot claim anyone is online.
      if (!days.length || start === null || end === null) return false;

      var now = localNow(this.dataset.timezone);
      if (!now) return false;
      if (days.indexOf(now.day) === -1) return false;

      return now.minutes >= start && now.minutes < end;
    }
  }

  /* Accepts "1,2,3,4,5" (Mon=1) or "Mon,Tue,Wed"; returns JS day numbers. */
  function parseDays(value) {
    if (!value) return [];

    return value.split(',').reduce(function (days, raw) {
      var token = raw.trim().toLowerCase();
      if (!token) return days;

      var asNumber = parseInt(token, 10);
      if (!isNaN(asNumber)) {
        days.push(asNumber === 7 ? 0 : asNumber);
        return days;
      }

      var index = DAY_NAMES.indexOf(token.slice(0, 3));
      if (index !== -1) days.push(index);

      return days;
    }, []);
  }

  /* "08:30" to minutes past midnight. */
  function parseTime(value) {
    if (!value) return null;

    var parts = value.trim().split(':');
    var hours = parseInt(parts[0], 10);
    var minutes = parseInt(parts[1], 10) || 0;

    if (isNaN(hours)) return null;

    return hours * 60 + minutes;
  }

  function localNow(timeZone) {
    var options = { weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' };
    if (timeZone) options.timeZone = timeZone;

    var parts;
    try {
      parts = new Intl.DateTimeFormat('en-US', options).formatToParts(new Date());
    } catch (error) {
      // An invalid timezone would otherwise throw on every tick.
      console.warn('Fit chat status: unusable timezone "' + timeZone + '"', error);
      return null;
    }

    var found = {};
    parts.forEach(function (part) { found[part.type] = part.value; });

    return {
      day: DAY_NAMES.indexOf(found.weekday.toLowerCase().slice(0, 3)),
      minutes: parseInt(found.hour, 10) * 60 + parseInt(found.minute, 10)
    };
  }

  customElements.define('fit-chat-status', FitChatStatus);

  /* Variant Fit config
     A variant metafield can point at a different Fit config. The map is
     rendered with the section. Switching color refetches the product only
     when that variant's handle is different from the one on the page.
     ====================================================================== */

  var fitRefreshToken = 0;

  function fitVariantMap() {
    var script = document.querySelector('[data-fit-variant-map]');
    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return null;
    }
  }

  function fitVariantId() {
    return new URLSearchParams(window.location.search).get('variant');
  }

  function replaceFitFromDocument(doc) {
    var currentSections = document.querySelectorAll('.section-fit-section');
    var nextSections = doc.querySelectorAll('.section-fit-section');
    currentSections.forEach(function (currentSection, index) {
      var nextSection = nextSections[index];
      if (!nextSection) return;
      currentSection.innerHTML = nextSection.innerHTML;
      if (window.customElements) customElements.upgrade(currentSection);
    });

    var currentPopup = document.querySelector('[data-fit-finder-popup]');
    var nextPopup = doc.querySelector('[data-fit-finder-popup]');

    if (currentPopup && currentPopup.classList.contains('active')) {
      document.body.classList.remove('fav-popup-open');
    }

    if (nextPopup) {
      if (currentPopup) currentPopup.replaceWith(nextPopup);
      else document.body.appendChild(nextPopup);
      if (window.customElements) customElements.upgrade(nextPopup);
    } else if (currentPopup) {
      currentPopup.remove();
    }
  }

  function refreshFitForVariant(variantId) {
    var token = ++fitRefreshToken;
    var url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);

    fetch(url.pathname + url.search, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('Fit section refresh failed');
        return response.text();
      })
      .then(function (html) {
        if (token !== fitRefreshToken) return;
        replaceFitFromDocument(new DOMParser().parseFromString(html, 'text/html'));
      })
      .catch(function (error) {
        console.warn('Fit section: could not load the variant config', error);
      });
  }

  document.body.addEventListener('product-url-change', function () {
    if (new URLSearchParams(window.location.search).has('fit_config')) return;

    var map = fitVariantMap();
    var variantId = fitVariantId();
    if (!map || map.locked || !variantId || !map.variants) return;

    var handle = map.variants[variantId];
    if (!handle || handle === map.active) return;

    refreshFitForVariant(variantId);
  });
})();
